<template>
    <v-card>
        <v-card-title class="text-h6" primary-title v-if="!('id' in props.modelValue)">
            {{ embedded ? "Request Processing" : "Create Request" }}
            <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
            <v-form v-model="localValid" validate-on="input">
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="localModel.protocol"
                            :items="protocols"
                            item-title="name"
                            return-object
                            label="Request Protocol"
                            required
                            :rules="[(value) => !!value || 'Request Protocol is required']"
                            @update:model-value="handleProtocolChange"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="6" v-if="localModel.protocol">
                        <v-autocomplete
                            label="Processor"
                            v-model="localModel.processor"
                            :items="requestProcessorOptions"
                            item-title="name"
                            return-object
                            required
                            :rules="[(value) => !!value || 'Processor is required']"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row v-if="isProtocolSelectedAndNeedsContainerTypes">
                    <v-col cols="6">
                        <v-autocomplete
                            label="Source Samples Container Type"
                            v-model="localModel.samplesContainerType"
                            :items="containerTypeOptions"
                            item-title="name"
                            return-object
                            required
                            :rules="[
                                (value) => !!value || 'Source Samples Container Type is required',
                            ]"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Request Processing Container Type"
                            v-model="localModel.requestContainerType"
                            :items="containerTypeOptions"
                            item-title="name"
                            return-object
                            required
                            :rules="[
                                (value) =>
                                    !!value || 'Request Processing Container Type is required',
                            ]"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="'id' in props.modelValue" cols="4">
                        <v-text-field
                            v-model="localModel.requestedByEmail"
                            label="Requested by Email (*)"
                            readonly
                        />
                    </v-col>
                    <v-col
                        v-if="'id' in props.modelValue"
                        cols="2"
                        align-self="center"
                        class="mb-3 ms-n3"
                    >
                        <v-btn
                            v-if="!requesterChanged"
                            variant="elevated"
                            color="primary"
                            @click="setRequesterToCurrentUser"
                        >
                            Set to myself
                        </v-btn>
                        <v-btn v-else variant="elevated" @click="resetRequester"> Reset </v-btn>
                    </v-col>
                    <v-col cols="6">
                        <v-date-input
                            v-model="localModel.needByDate"
                            label="Need by date"
                            :rules="[
                                (value) =>
                                    (!!value && !!localModel.needByDate) ||
                                    'Need by date is required if task is created',
                            ]"
                            :min="new Date().toISOString().substring(0, 10)"
                            :max="
                                new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                                    .toISOString()
                                    .substring(0, 10)
                            "
                        ></v-date-input>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="'id' in props.modelValue" cols="6">
                        <v-autocomplete
                            v-model="localModel.status"
                            :items="requestStatusOptions"
                            label="Status"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions v-if="!embedded">
            <v-spacer></v-spacer>
            <v-btn @click="cancel" color="primary">Cancel</v-btn>
            <v-btn @click="handleSave" color="primary" :disabled="!localValid">Save Request</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from "vue";
import {
    RequestProcessor,
    RequestProtocol,
    RequestStatus,
    Request,
    samplesAPI,
    SavedRequestProtocol,
    BaseRequest,
    SavedRequest,
} from "@/services/samples/samplesAPI";
import { withLoadingScreen } from "@/lib/loading";
import { useAuth } from "@/lib/ts/auth";
import { ContainerType } from "@/services/commonModels";

const auth = useAuth();
const user = computed(() => auth.getAuthUser());

const props = defineProps<{
    modelValue: Request | SavedRequest;
    valid: boolean;
    embedded: boolean;
    protocols?: SavedRequestProtocol[];
    processors?: RequestProcessor[];
    containerTypes?: ContainerType[];
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: Request): void;
    (e: "update:valid", value: boolean): void;
    (e: "canceled"): void;
    (e: "saved", value: Request): void;
}>();

onMounted(() => {
    if (!props.protocols) {
        fetchRequestProtocolOptions();
    }
    if (!props.processors) {
        fetchRequestProcessorOptions();
    }
    if (!props.containerTypes) {
        fetchContainerTypeOptions();
    }
});

const { modelValue } = toRefs(props);
const localModel = ref({
    ...modelValue.value,
    needByDate: modelValue.value.needByDate ? new Date(modelValue.value.needByDate) : null
});
const localValid = ref(props.valid);

const requestProtocolOptions = ref<RequestProtocol[]>(props.protocols ?? []);
const requestProcessorOptions = ref<RequestProcessor[]>(props.processors ?? []);
const containerTypeOptions = ref<ContainerType[]>(props.containerTypes ?? []);

const initialRequesterValue = ref<string | null>(props.modelValue.requestedByEmail ?? null);
const requesterChanged = ref<boolean>(false);

watch(
    localModel,
    (val) => {
        emit("update:modelValue", val);
    },
    { deep: true }
);
watch(localValid, (val) => emit("update:valid", !!val));
watch(modelValue, (val) => {
    // keep in sync if parent changes modelValue; this is useful for resetting the form
    if (val && val !== localModel.value) localModel.value = structuredClone(val);
});

const isProtocolSelectedAndNeedsContainerTypes = computed(() => {
    return (
        localModel.value.protocol &&
        localModel.value.processor &&
        localModel.value.processor.type === "internal"
    );
});

function cancel() {
    emit("canceled");
}

async function handleSave() {
    await withLoadingScreen(async () => {
        const savedRequest = await saveRequest();
        emit("saved", savedRequest);
    });
}

async function saveRequest(): Promise<Request> {
    const req = localModel.value;

    const requestToSave: BaseRequest = {
        experimentCode: req.experimentCode,
        sourceSampleSetId: req.sourceSampleSet!.id,
        protocolId: req.protocol!.id,
        needByDate: req.needByDate,
        requestedByEmail: req.requestedByEmail,
        requestContainerTypeId: req.requestContainerType?.id || null,
        samplesContainerTypeId: req.samplesContainerType?.id || null,
        processorId: req.processor!.id,
        status: req.status ?? RequestStatus.DRAFT, // default to draft
    };

    if ("id" in req) {
        return samplesAPI.updateRequest(req.id, requestToSave);
    } else {
        return samplesAPI.createRequest({
            ...requestToSave,
            requestedByEmail: user.value?.email,
        });
    }
}

function setRequesterToCurrentUser(): void {
    localModel.value.requestedByEmail = user.value.email;
    requesterChanged.value = true;
}

function resetRequester(): void {
    localModel.value.requestedByEmail = initialRequesterValue.value ?? "";
    requesterChanged.value = false;
}

async function fetchRequestProtocolOptions() {
    await withLoadingScreen(async () => {
        requestProtocolOptions.value = await samplesAPI.fetchRequestProtocols();
    });
}

async function fetchRequestProcessorOptions() {
    await withLoadingScreen(async () => {
        requestProcessorOptions.value = await samplesAPI.fetchRequestProcessors();
    });
}

async function fetchContainerTypeOptions() {
    await withLoadingScreen(async () => {
        containerTypeOptions.value = await samplesAPI.fetchRequestContainerTypes();
    });
}

function handleProtocolChange(protocol: SavedRequestProtocol | null) {
    localModel.value.protocol = protocol;
    // reset processor and container types when protocol changes
    localModel.value.processor = null;
    localModel.value.requestContainerType = null;
    localModel.value.samplesContainerType = null;

    if (protocol?.requestProcessorId) {
        const matchedProcessor = requestProcessorOptions.value.find(
            (processor) => processor.id === protocol.requestProcessorId
        );
        if (matchedProcessor) {
            localModel.value.processor = matchedProcessor;
            if (matchedProcessor.type === "internal") {
                // if internal processor, and protocol has container types, set them
                localModel.value.samplesContainerType = findContainerType(
                    protocol.samplesContainerTypeId
                );
                localModel.value.requestContainerType = findContainerType(
                    protocol.requestContainerTypeId
                );
            }
        }
    }
}

function findContainerType(containerTypeId: string | null): ContainerType | null {
    if (!containerTypeId) {
        return null;
    }
    return containerTypeOptions.value.find((ct) => ct.id === containerTypeId) || null;
}

const requestStatusOptions = ref([
    "draft",
    "canceled",
    "awaiting processing",
    "in progress",
    "completed",
]);

defineExpose({
    saveRequest: saveRequest,
});
</script>
